import { PointOfSaleItem } from '../model/pointofsale/PointOfSaleItem';

/**
 * Gets the rank value from a PointOfSaleItem's metadata
 * @param item The PointOfSaleItem
 * @returns The rank as a number, or Infinity if not available/invalid
 */
export const getItemRank = (item: PointOfSaleItem): number => {
  const rankStr = item.metadata?.Rank;
  if (rankStr) {
    const rank = parseInt(rankStr, 10);
    return isNaN(rank) ? Infinity : rank;
  }
  return Infinity;
};

/**
 * Gets the transaction count from a PointOfSaleItem's metadata
 * @param item The PointOfSaleItem
 * @returns The transaction count as a number, or 0 if not available/invalid
 */
export const getItemTransactionCount = (item: PointOfSaleItem): number => {
  const countStr = item.metadata?.TransactionCount;
  if (countStr) {
    const count = parseInt(countStr, 10);
    return isNaN(count) ? 0 : count;
  }
  return 0;
};

/**
 * Generates a tooltip string from a PointOfSaleItem's metadata
 * @param item The PointOfSaleItem
 * @returns A formatted string with rank and/or transaction count, or empty string if none available
 */
export const getItemTooltip = (item: PointOfSaleItem): string => {
  const parts: string[] = [];
  if (item.metadata?.Rank) {
    parts.push(`Rank: ${item.metadata.Rank}`);
  }
  if (item.metadata?.TransactionCount) {
    parts.push(`Transaction Count: ${item.metadata.TransactionCount}`);
  }
  return parts.length > 0 ? parts.join(', ') : '';
};

/**
 * Sorts an array of PointOfSaleItems based on the specified sort type
 * @param items The items to sort
 * @param sortType The sort type ('rank' or 'mostPopular')
 * @returns A new sorted array
 */
export const sortPosItems = <T extends PointOfSaleItem>(
  items: T[],
  sortType: 'rank' | 'mostPopular'
): T[] => {
  const sorted = [...items];
  if (sortType === 'rank') {
    sorted.sort((a, b) => {
      const rankA = getItemRank(a);
      const rankB = getItemRank(b);
      if (rankA === rankB) {
        return a.name.localeCompare(b.name);
      }
      return rankA - rankB;
    });
  } else {
    // mostPopular
    sorted.sort((a, b) => {
      const countA = getItemTransactionCount(a);
      const countB = getItemTransactionCount(b);
      if (countA === countB) {
        return a.name.localeCompare(b.name);
      }
      return countB - countA; // Descending order
    });
  }
  return sorted;
};

